import clsx from 'clsx';
import type { ButtonHTMLAttributes, CSSProperties } from 'react';
import { forwardRef } from 'react';

import styles from './GlassButton.module.scss';

type GlassButtonShape = 'default' | 'square' | 'circle';

export type GlassButtonProps = {
  size?: number | string;
  shape?: GlassButtonShape;
  fullWidth?: boolean;
  /**
   * The control has done its thing — kept, vouched, going. Fills teal with dark ink, so
   * the acted state reads without a label change and holds up over a photograph.
   */
  done?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const toCssSize = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value;

const GlassButton = forwardRef<HTMLButtonElement, GlassButtonProps>(
  (
    {
      type = 'button',
      size = 36,
      shape = 'default',
      fullWidth = false,
      done = false,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const shapeClass = {
      default: styles.shapeDefault,
      square: styles.shapeSquare,
      circle: styles.shapeCircle,
    }[shape];

    const cssSize = toCssSize(size);
    const buttonStyle: CSSProperties = {
      ...style,
      ['--glass-button-size' as string]: cssSize,
    };

    return (
      <button
        ref={ref}
        type={type}
        className={clsx(
          styles.button,
          shapeClass,
          fullWidth && styles.fullWidth,
          done && styles.done,
          className,
        )}
        style={buttonStyle}
        {...props}
      />
    );
  },
);

GlassButton.displayName = 'GlassButton';

export default GlassButton;
